/**
 * Route index for site search.
 *
 * Filings, press releases and documents are searched on the server — they are
 * the disclosure record and must come from the database. This file only makes
 * the site's own pages findable, so "warranty" or "resumption" lands somewhere
 * useful. Keywords are search aids, never published copy.
 */
export const SITE_PAGES = [
  {
    title: 'Home',
    url: '/',
    summary: 'The Group at a glance: strategy, businesses and the latest filings.',
    keywords: 'overview usp group suprai landing latest announcements',
  },
  {
    title: 'Investor Relations',
    url: '/investor-relations',
    summary: 'Announcements, results, share information and governance disclosures.',
    keywords: 'ir investors shareholders disclosure sgxnet',
  },
  {
    title: 'Announcements',
    url: '/investor-relations/announcements',
    summary: 'Every regulatory announcement released via SGXNet.',
    keywords: 'sgxnet filings disclosure record pdf',
  },
  {
    title: 'Financial results',
    url: '/investor-relations/financial-results',
    summary: 'Results announcements, presentations and webcasts by period.',
    keywords: 'results centre half year full year fy h1 revenue earnings',
  },
  {
    title: 'Annual reports',
    url: '/investor-relations/annual-reports',
    summary: 'The Group’s annual reporting archive.',
    keywords: 'annual report archive',
  },
  {
    title: 'Verified documents',
    url: '/investor-relations/verified-documents',
    summary:
      'Blockchain-sealed disclosure documents, their SHA-256 fingerprints, and a browser-side check for any copy.',
    keywords: 'verified blockchain opentimestamps sha256 fingerprint seal proof authenticity',
  },
  {
    title: 'Share information',
    url: '/investor-relations/share-information',
    summary: 'Listing, ticker, ISIN, trading status and the resumption roadmap.',
    keywords: 'brs isin board lot share price trading suspension resumption timeline progress',
  },
  {
    title: 'General meetings',
    url: '/investor-relations/general-meetings',
    summary: 'Notices, proxy forms and results of meetings.',
    keywords: 'agm egm proxy notice minutes',
  },
  {
    title: 'Corporate actions',
    url: '/investor-relations/corporate-actions',
    summary: 'Restructuring, capital actions and the resumption of trading.',
    keywords: 'scheme of arrangement judicial management restructuring loan conversion disposal',
  },
  {
    title: 'Email alerts',
    url: '/investor-relations/email-alerts',
    summary: 'Announcements and results, by email.',
    keywords: 'subscribe alerts notifications',
  },
  {
    title: 'IR contact',
    url: '/investor-relations/contact',
    summary: 'Investor and analyst enquiries.',
    keywords: 'investor relations contact enquiries analyst',
  },
  {
    title: 'The Group',
    url: '/the-group',
    summary: 'Strategy, pillars, the flywheel, milestones and group structure.',
    keywords: 'strategy pillars flywheel milestones structure subsidiaries leadership',
  },
  {
    title: 'Our Businesses',
    url: '/our-businesses',
    summary: 'Supratechnic, SII, SupraYi, SuprAI and the technology pillar.',
    keywords:
      'supratechnic hidea tohatsu honda marine multiflex seafirst outboard marine warranty platform sii suprayi suprai fishing ocean tokenchain',
  },
  {
    title: 'Governance',
    url: '/governance',
    summary: 'Board, committees and governance policies.',
    keywords: 'board directors committees audit risk whistleblowing policies charters',
  },
  {
    title: 'Sustainability',
    url: '/sustainability',
    summary: 'The Group’s approach to sustainability.',
    keywords: 'esg sustainability environment social governance',
  },
  {
    title: 'Newsroom',
    url: '/newsroom',
    summary: 'Press releases and media enquiries.',
    keywords: 'press releases media news stories',
  },
  {
    title: 'Intelligent Fishing Application',
    url: '/technology/intelligent-fishing-application',
    summary: 'The Group’s technology platform.',
    keywords: 'technology platform intelligent fishing application ocean data',
  },
  {
    title: 'Contact',
    url: '/contact',
    summary: 'Offices, general enquiries and how to reach the Group.',
    keywords: 'offices address enquiries email phone singapore',
  },
  {
    title: 'Privacy Policy',
    url: '/privacy-policy',
    summary:
      'How the Group collects, uses, discloses and retains personal data under the PDPA. Draft pending counsel sign-off.',
    keywords:
      'privacy pdpa data protection dpo cookies consent retention breach notification dnc do not call transfer singapore malaysia indonesia gdpr subject access rights',
  },
  {
    title: 'Terms of Use',
    url: '/terms-of-use',
    summary:
      'The terms governing use of this website, including the SGXNet-prevails rule, forward-looking statements and market-data limits. Draft pending counsel sign-off.',
    keywords:
      'terms conditions legal permitted use prohibited use intellectual property trade marks liability indemnity forward-looking sgxnet market data governing law singapore',
  },
  {
    title: 'Disclaimer',
    url: '/disclaimer',
    summary:
      'Content and website liability: accuracy, currency, third-party material, brand-partner references and availability. Draft pending counsel sign-off.',
    keywords:
      'disclaimer legal accuracy completeness currency no advice no offer subsidiaries brand partners certifications third party websites availability liability governing law',
  },
  {
    title: 'Cookie Policy',
    url: '/cookie-policy',
    summary:
      'What this site stores in your browser, the consent banner, Global Privacy Control and how to change your choices. Draft pending counsel sign-off.',
    keywords:
      'cookies consent tracking banner local storage session storage analytics preferences global privacy control gpc do not track retention pdpa inventory',
  },
  {
    title: 'Important Notice',
    url: '/important-notice',
    summary:
      'The securities-law gateway notice: SGXNet primacy, forward-looking statements, offer restrictions, US restrictions, trading suspension and restructuring status. Draft pending counsel sign-off.',
    keywords:
      'important notice forward looking statements sgxnet authoritative no offer solicitation united states securities act regulation s trading suspension judicial management scheme of arrangement rumours fraud warning market data',
  },
];

/** Client-side page matches, scored so a title hit always outranks a keyword hit. */
export function matchPages(query, max = 4) {
  const needle = (query || '').trim().toLowerCase();
  if (needle.length < 2) return [];
  return SITE_PAGES.map((page) => {
    const title = page.title.toLowerCase();
    const haystack = `${title} ${page.summary} ${page.keywords}`.toLowerCase();
    if (!haystack.includes(needle)) return null;
    const score = title.startsWith(needle) ? 24 : title.includes(needle) ? 22 : 12;
    return {
      type: 'page',
      title: page.title,
      url: page.url,
      snippet: page.summary,
      date: null,
      score,
    };
  })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score)
    .slice(0, max);
}
